import type {
  AppetiteModel as Appetite_Model_Enum,
  AppetiteModel,
} from '@risksmart-app/domain/src/types/consts/appetite-model';
import type {
  RiskScoringModel as Risk_Scoring_Model_Enum,
  RiskScoringModel,
} from '@risksmart-app/domain/src/types/consts/risk-scoring-model';
import type { UpdateAggregationSettingsForOrgMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateAggregationSettingsForOrgDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

// The web layer speaks the GraphQL-generated enums (what every call site uses);
// the tRPC procedure input is typed with the domain enums (z.nativeEnum). The
// string values are identical, so the public input takes the GraphQL enums and
// we cast to the domain enums at the single mapInput boundary below. The
// public input shape keeps `AppetiteCascadingModel` so existing call sites
// don't need to change; the tRPC procedure takes it as `Appetite`.
export interface UpdateAggregationSettingsForOrgInput {
  RiskScoringModel?: Risk_Scoring_Model_Enum | null;
  AppetiteCascadingModel?: Appetite_Model_Enum | null;
  Config?: Record<string, unknown> | null;
}

export const useUpdateAggregationSettingsForOrg = createMutationHook({
  name: 'updateAggregationSettingsForOrg',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.aggregation.updateAggregationSettingsForOrg.mutationOptions({
      onSuccess,
    }),
  mapInput: (input: UpdateAggregationSettingsForOrgInput) => ({
    RiskScoringModel: (input.RiskScoringModel ??
      null) as RiskScoringModel | null,
    Appetite: (input.AppetiteCascadingModel ?? null) as AppetiteModel | null,
    Config: input.Config ?? null,
  }),
  invalidate: (trpc) => [
    trpc.frontend.aggregation.getAggregationSettingsForOrg.queryKey(),
  ],
  // Maps the tRPC mutation response to match the GraphQL mutation response
  // shape so consumers branching on the result do not need to know which
  // path executed.
  mapResponse: (result: {
    affected_rows: number;
  }): UpdateAggregationSettingsForOrgMutation => ({
    insert_aggregation_org: {
      __typename: 'aggregation_org_mutation_response',
      affected_rows: result.affected_rows,
    },
  }),
  graphqlDocument: UpdateAggregationSettingsForOrgDocument,
  graphqlVariables: (input: UpdateAggregationSettingsForOrgInput) => ({
    RiskScoringModel: input.RiskScoringModel ?? null,
    AppetiteCascadingModel: input.AppetiteCascadingModel ?? null,
    Config: input.Config ?? null,
  }),
  evictFields: ['aggregation_org'],
});
